import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import FaRegisterSchedule from "../../../components/Schedules/CA/FaRegister/FaRegisterSchedule";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { getCaSettings } from "../../../builders/settings/CaSetting";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { CA_SCHEDULES } from "../../../utils/data/controlData";

const parseDoc = (doc) => {
  if (!doc) return {};
  if (typeof doc === "object") return doc;

  try {
    return JSON.parse(doc);
  } catch {
    return {};
  }
};

const FaRegisterSchedulePage = forwardRef(
  ({ company, scheduleId, openScheduleWithDelay }, ref) => {
    const {
      getCAScheduleDataByType,
      updateCASchedule,
      deleteCAScheduleByType,
    } = useContext(AuthContext);
    const [schedule, setSchedule] = useState({});
    const [scheduleDetails, setScheduleDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [isDirty, setIsDirty] = useState(false);
    const [shouldConfirmExit, setShouldConfirmExit] = useState(false);
    const isDirtyRef = useRef(false);
    const unSavedDataRef = useRef(null);

    const { canEdit: canEditCASchedule } = useAccessControl(CA_SCHEDULES);
    const handleError = useErrorDialog();
    const handleSuccess = useSuccessDialog();

    useImperativeHandle(ref, () => ({
      isDirty: () => isDirtyRef.current,
      shouldConfirmExit: () => shouldConfirmExit,
      getUnsavedData: () => unSavedDataRef.current,
      clearDirtyFlag: () => {
        isDirtyRef.current = false;
        setIsDirty(false);
        setShouldConfirmExit(false);
        unSavedDataRef.current = null;
      },
    }));

    const recordChangeHappened = () => {
      isDirtyRef.current = true;
      setIsDirty(true);
    };
    const recordSaved = () => {
      isDirtyRef.current = false;
      setIsDirty(false);
      setShouldConfirmExit(false);
      unSavedDataRef.current = null;
    };
    const recordUnsavedData = (data) => {
      unSavedDataRef.current = {
        ...data,
        sch_id: scheduleId,
        scheduleType: "FARegister",
      };
    };

    useEffect(() => {
      const handleBeforeUnload = (event) => {
        if (!isDirty) return;

        event.preventDefault();
        event.returnValue = "";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }, [isDirty]);

    const saveSchedule = async (scheduleDetails) => {
      if (!company || !scheduleId) {
        handleError("Missing UEN or Schedule ID. Please try again.");
        return false;
      }

      const updatedSchedule = {
        sch_id: scheduleId,
        scheduleType: "FARegister",
        assetCategories: scheduleDetails.assetCategories,
        dataRows: scheduleDetails.dataRows,
        summaries: scheduleDetails.summaries,
        doc: scheduleDetails.doc,
        notes: scheduleDetails.notes,
        reviewNotes: scheduleDetails.reviewNotes,
      };

      try {
        setLoading(true);
        const response = await updateCASchedule(updatedSchedule);

        if (response) {
          handleSuccess("The schedule has been saved successfully.");
          setReloadKey((key) => key + 1);
          return true;
        }

        handleError(
          "There was a problem saving the schedule. Please contact support if the issue persists."
        );
        return false;
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setLoading(false);
      }
    };

    const deleteSchedule = async () => {
      if (!scheduleId) {
        handleError("Missing Schedule ID.");
        return;
      }

      setLoading(true);
      const response = await deleteCAScheduleByType({ sch_id: scheduleId });
      setLoading(false);

      if (response) {
        handleSuccess("Schedule deleted successfully.");
        localStorage.removeItem("selectedSection");
        localStorage.setItem("selectedComponent", "Detailed P&L");
        window.location.reload();
      } else {
        handleError(
          "There was a problem deleting the schedule. Please contact support if the issue persists."
        );
      }
    };

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);

        try {
          const response = await getCAScheduleDataByType(
            scheduleId,
            "FARegister"
          );
          const scheduleData = response?.schedule || {};
          const doc = parseDoc(scheduleData.doc);
          const caSettings = getCaSettings();

          setSchedule({
            id: scheduleId,
            type: "Fixed Asset Register",
            scheduleNo: scheduleData.scheduleNo,
            name: "Fixed Asset Register",
            caSettings: scheduleData.caSetting || caSettings,
            notes: scheduleData.notes || doc.notes || "",
            reviewNotes: scheduleData.reviewNotes || doc.reviewNotes || "",
          });

          setScheduleDetails({
            assetCategories:
              scheduleData.assetCategories || doc.assetCategories || [],
            dataRows: scheduleData.dataRows || doc.assetRows || [],
            summaries: scheduleData.summaries || doc.summaries || {},
            doc,
          });
          recordSaved();
        } catch (error) {
          handleError(error);
        } finally {
          setLoading(false);
        }
      };

      if (company && scheduleId) {
        fetchData();
      }
    }, [
      scheduleId,
      company?.tc_id,
      reloadKey,
    ]);

    return loading ? (
      <ComponentLoading />
    ) : (
      <FaRegisterSchedule
        company={company}
        schedule={schedule}
        scheduleDetails={scheduleDetails}
        saveSchedule={saveSchedule}
        deleteSchedule={deleteSchedule}
        recordSaved={recordSaved}
        recordUnsavedData={recordUnsavedData}
        recordChangeHappened={recordChangeHappened}
        openScheduleWithDelay={openScheduleWithDelay}
        canEditSchedule={canEditCASchedule}
      />
    );
  }
);

export default FaRegisterSchedulePage;
